import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FaExchangeAlt, FaSearch, FaTruck } from 'react-icons/fa';

import Loading from '../../../../components/Loading';
import PaginationBar from '../../../../components/PaginationBar/PaginationBar';
import usePermissions from '../../../../components/Menu/MenuItems/hooks/usePermissions';
import { useAllPdvs } from '../../../../hooks/useAllPdvs';
import { listTransferOrders } from '../../../../services/inventory/transfersApi';
import { notifyApiError } from '../../../../utils/feedback';

import { Container } from '../../../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderActions,
   PageHeaderText,
   PageSubtitle,
   PageTitle,
} from '../../../../styles/layout/PageHeader';
import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../../../styles/layout/Section';
import {
   ActionBar,
   ActionBarLeft,
   ActionBarRight,
} from '../../../../styles/layout/ActionBar';
import { FormGrid } from '../../../../styles/layout/FormGrid';
import { Field } from '../../../../styles/forms/Field';
import { TableWrapper } from '../../../../styles/table/TableWrapper';
import { DataTable } from '../../../../styles/ui/Table';
import { EmptyState } from '../../../../styles/feedback/EmptyState';
import { ClickableRow } from '../../../../styles/ui/Link';
import {
   CtaButton,
   CtaOnDarkButton,
   GhostButton,
   GhostOnDarkButton,
   SmallButton,
} from '../../../../styles/ui/Button';

import { OrderStatusBadge } from '../components/StatusBadges';
import {
   ITEMS_PER_PAGE,
   ORDER_SORTABLE_KEYS,
   ORDER_STATUS,
   ORDER_STATUS_OPTIONS,
   TRANSFER_PERMISSIONS,
} from '../constants';
import { formatDateTimeBr } from '../utils/format';

// ---------------------------------------------------------------------------
// Listagem de ordens de transferência (GET /transfer-orders).
//
// Segue o Capítulo 1 do `navegacao.md`: URL é a fonte da verdade, busca só no
// submit/paginação, sentinela `applied=1`, `replace: true` no histórico.
//
// Refatoração visual 2026-09-10 (protótipo do handoff de design):
//   · tabela na camada nova (`styles/ui/Table`), com Origem e Destino em DUAS
//     colunas — a rota com seta ficou exclusiva do header do detalhe;
//   · dois estados vazios com cópia distinta: "não pesquisei" (com atalhos de
//     busca real: aguardando expedição / parciais / tudo dos meus PDVs) ×
//     "pesquisei e não achou" (com Limpar);
//   · o campo "Buscar nº da ordem" do protótipo NÃO entrou: o contrato só
//     filtra por status e pdv_id — campo sem backend é filtro fantasma.
//
// **Ordenação habilitada em 2026-08-21** (adendo 0099 §4.3): `ORDER_SORTABLE_KEYS`
// espelha exatamente a whitelist de `order_by` do backend (§1.10), e só os `<th>`
// dessa lista são clicáveis. Default do backend = `id desc`, então a ordenação
// só vai para a URL quando difere dele. Clicar em "Situação" ordena pelo ciclo
// de vida do ENUM (OPEN → PARTIAL → CLOSED → CANCELLED), não alfabeticamente.
//
// Uma ausência deliberada permanece: SEM filtro por `kind` — a Fase 1 é
// PUSH-only; o param existe no service (schema PULL-ready), mas oferecer o
// filtro na tela sugeriria um universo que ainda não existe.
//
// O badge "tratativa pendente" também não aparece aqui: na listagem o backend
// devolve `has_pending_divergence: null` (remessas não carregadas). Ele só é
// confiável no detalhe.
// ---------------------------------------------------------------------------

const DEFAULT_FILTERS = {
   status: '',
   pdv_id: '',
};

const NOT_SORTABLE_TITLE = 'Este campo não é ordenável pelo servidor';

function readFiltersFromUrl(searchParams) {
   const next = { ...DEFAULT_FILTERS };
   Object.keys(DEFAULT_FILTERS).forEach((key) => {
      const value = searchParams.get(key);
      if (value !== null && value !== '') next[key] = value;
   });
   return next;
}

// Sem coluna ativa, `order_by`/`order_dir` não são enviados — o backend aplica
// o próprio default (`id desc`).
function toServiceArgs(filters, sort, page) {
   return {
      page,
      perPage: ITEMS_PER_PAGE,
      status: filters.status,
      pdvId: filters.pdv_id,
      orderBy: sort.key ?? undefined,
      orderDir: sort.key ? sort.direction : undefined,
   };
}

function readSortFromUrl(searchParams) {
   const orderBy = searchParams.get('order_by');
   if (orderBy && ORDER_SORTABLE_KEYS.has(orderBy)) {
      return {
         key: orderBy,
         direction: searchParams.get('order_dir') === 'desc' ? 'desc' : 'asc',
      };
   }
   return { key: null, direction: null };
}

export default function TransferOrderList() {
   const navigate = useNavigate();
   const { hasPermission } = usePermissions();
   const canCreate = hasPermission(TRANSFER_PERMISSIONS.DISPATCH);

   const { pdvs } = useAllPdvs();

   const [searchParams, setSearchParams] = useSearchParams();
   const hasInitialQueryParams = searchParams.toString() !== '';

   const [filters, setFilters] = useState(() =>
      readFiltersFromUrl(searchParams)
   );
   const [appliedFilters, setAppliedFilters] = useState(filters);
   const [sortConfig, setSortConfig] = useState(() =>
      readSortFromUrl(searchParams)
   );
   const [currentPage, setCurrentPage] = useState(() => {
      const fromUrl = Number(searchParams.get('page'));
      return Number.isFinite(fromUrl) && fromUrl > 0 ? fromUrl : 1;
   });

   const [orders, setOrders] = useState([]);
   const [totalPages, setTotalPages] = useState(1);
   const [totalCount, setTotalCount] = useState(0);
   const [isLoading, setIsLoading] = useState(false);
   // Separa os dois vazios: abertura fria (convite a pesquisar) × busca sem
   // resultado (limpar filtros).
   const [hasSearched, setHasSearched] = useState(hasInitialQueryParams);

   const didInitialFetch = useRef(false);

   const commitToUrl = useCallback(
      (values, sort, page) => {
         const params = { applied: '1' };
         Object.keys(DEFAULT_FILTERS).forEach((key) => {
            if (values[key]) params[key] = values[key];
         });
         // Ordenação só vai pra URL quando difere do default do backend.
         if (sort.key) {
            params.order_by = sort.key;
            params.order_dir = sort.direction;
         }
         if (page > 1) params.page = String(page);
         setSearchParams(params, { replace: true });
      },
      [setSearchParams]
   );

   const fetchOrders = useCallback(async (values, sort, page) => {
      setIsLoading(true);
      setHasSearched(true);
      try {
         const result = await listTransferOrders(
            toServiceArgs(values, sort, page)
         );
         setOrders(result.orders);
         setTotalPages(result.totalPages);
         setTotalCount(result.total);
         setCurrentPage(result.page);
      } catch (err) {
         notifyApiError(err, {
            fallback: 'Erro ao buscar transferências.',
         });
      } finally {
         setIsLoading(false);
      }
   }, []);

   // Busca inicial só com query params (deep-link / F5 / voltar-do-detalhe).
   useEffect(() => {
      if (didInitialFetch.current) return;
      didInitialFetch.current = true;
      if (!hasInitialQueryParams) return;
      fetchOrders(appliedFilters, sortConfig, currentPage);
   }, [
      fetchOrders,
      hasInitialQueryParams,
      appliedFilters,
      sortConfig,
      currentPage,
   ]);

   const updateField = (key, value) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
   };

   const runSearch = (values) => {
      // Padrão BillingCharges/ClienteList: nova busca por filtros zera a
      // ordenação de volta ao default do backend.
      const resetSort = { key: null, direction: null };
      setFilters(values);
      setAppliedFilters(values);
      setSortConfig(resetSort);
      commitToUrl(values, resetSort, 1);
      fetchOrders(values, resetSort, 1);
   };

   const handleSearch = (event) => {
      event.preventDefault();
      runSearch(filters);
   };

   // Atalhos do estado vazio — cada um é uma busca REAL com filtros que o
   // backend aceita (nada de campo fantasma).
   const shortcuts = [
      {
         label: 'Aguardando expedição',
         values: { ...DEFAULT_FILTERS, status: ORDER_STATUS.OPEN },
      },
      {
         label: 'Parciais',
         values: { ...DEFAULT_FILTERS, status: ORDER_STATUS.PARTIAL },
      },
      { label: 'Tudo dos meus PDVs', values: { ...DEFAULT_FILTERS } },
   ];

   const handleClear = () => {
      setFilters(DEFAULT_FILTERS);
      setAppliedFilters(DEFAULT_FILTERS);
      setSortConfig({ key: null, direction: null });
      setOrders([]);
      setTotalCount(0);
      setTotalPages(1);
      setCurrentPage(1);
      setHasSearched(false);
      setSearchParams({}, { replace: true });
   };

   // Clique no cabeçalho: inativa → asc → desc → inativa (volta ao default do
   // backend). Usa os filtros já APLICADOS, não o rascunho, e volta pra pág. 1.
   const handleSort = (key) => {
      if (!ORDER_SORTABLE_KEYS.has(key)) return;

      let nextSort;
      if (sortConfig.key === key) {
         nextSort =
            sortConfig.direction === 'asc'
               ? { key, direction: 'desc' }
               : { key: null, direction: null };
      } else {
         nextSort = { key, direction: 'asc' };
      }

      setSortConfig(nextSort);
      commitToUrl(appliedFilters, nextSort, 1);
      fetchOrders(appliedFilters, nextSort, 1);
   };

   const getSortIcon = (key) => {
      if (sortConfig.key !== key) return ' ⇅';
      return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
   };

   const sortableThProps = (key, title) => ({
      className: `sortable${sortConfig.key === key ? ' sort-active' : ''}`,
      onClick: () => handleSort(key),
      title,
   });

   const handlePageChange = (page) => {
      commitToUrl(appliedFilters, sortConfig, page);
      fetchOrders(appliedFilters, sortConfig, page);
   };

   const hasResults = orders.length > 0;

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Transferências entre PDVs</PageTitle>
               <PageSubtitle>
                  {hasSearched && totalCount
                     ? `${totalCount} ordem(ns) no filtro atual`
                     : 'Ordens de transferência — pedido, expedição e conferência'}
               </PageSubtitle>
            </PageHeaderText>
            <PageHeaderActions>
               <GhostOnDarkButton as={Link} to="/inventory/shipments">
                  <FaTruck size={12} aria-hidden="true" /> Remessas em trânsito
               </GhostOnDarkButton>
               {canCreate && (
                  <CtaOnDarkButton as={Link} to="/inventory/transfers/new">
                     + Nova transferência
                  </CtaOnDarkButton>
               )}
            </PageHeaderActions>
         </PageHeader>

         <Section>
            <SectionHeader>
               <SectionTitle>Filtros</SectionTitle>
            </SectionHeader>
            <SectionBody>
               <form onSubmit={handleSearch}>
                  <FormGrid>
                     <Field className="col-4">
                        <label htmlFor="filter-status">Situação</label>
                        <select
                           id="filter-status"
                           value={filters.status}
                           onChange={(event) =>
                              updateField('status', event.target.value)
                           }
                        >
                           <option value="">Todas</option>
                           {ORDER_STATUS_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                 {option.label}
                              </option>
                           ))}
                        </select>
                     </Field>

                     <Field className="col-4">
                        <label htmlFor="filter-pdv">PDV (origem ou destino)</label>
                        <select
                           id="filter-pdv"
                           value={filters.pdv_id}
                           onChange={(event) =>
                              updateField('pdv_id', event.target.value)
                           }
                        >
                           <option value="">Todos os meus PDVs</option>
                           {pdvs.map((pdv) => (
                              <option key={pdv.id} value={pdv.id}>
                                 {pdv.pdv_name ?? pdv.name ?? `PDV ${pdv.id}`}
                              </option>
                           ))}
                        </select>
                     </Field>
                  </FormGrid>

                  <ActionBar style={{ marginTop: 16 }}>
                     <ActionBarLeft />
                     <ActionBarRight>
                        <GhostButton
                           type="button"
                           onClick={handleClear}
                           disabled={isLoading}
                        >
                           Limpar
                        </GhostButton>
                        <CtaButton type="submit" disabled={isLoading}>
                           <FaSearch size={12} aria-hidden="true" /> Pesquisar
                        </CtaButton>
                     </ActionBarRight>
                  </ActionBar>
               </form>
            </SectionBody>
         </Section>

         <Section>
            <SectionHeader>
               <SectionTitle>
                  Ordens{totalCount ? ` · ${totalCount} resultado(s)` : ''}
               </SectionTitle>
            </SectionHeader>
            <SectionBody>
               {hasResults && (
                  <>
                     <TableWrapper $minWidth="940px">
                        <DataTable>
                           {/* Só as colunas de ORDER_SORTABLE_KEYS são
                               clicáveis; as demais são cabeçalho comum
                               (navegacao.md §1.10). `updated_at` está na
                               whitelist mas não tem coluna — sem coluna, sem
                               ordenação. */}
                           <thead>
                              <tr>
                                 <th
                                    className={`center sortable${sortConfig.key === 'id' ? ' sort-active' : ''}`}
                                    onClick={() => handleSort('id')}
                                    title="Ordenar por número da ordem"
                                 >
                                    #{getSortIcon('id')}
                                 </th>
                                 <th title={NOT_SORTABLE_TITLE}>Origem</th>
                                 <th title={NOT_SORTABLE_TITLE}>Destino</th>
                                 <th className="num" title={NOT_SORTABLE_TITLE}>
                                    Itens
                                 </th>
                                 <th title={NOT_SORTABLE_TITLE}>Criada por</th>
                                 <th
                                    {...sortableThProps(
                                       'created_at',
                                       'Ordenar por data de criação'
                                    )}
                                 >
                                    Criada em{getSortIcon('created_at')}
                                 </th>
                                 <th
                                    {...sortableThProps(
                                       'closed_at',
                                       'Ordenar por data de conclusão'
                                    )}
                                 >
                                    Concluída em{getSortIcon('closed_at')}
                                 </th>
                                 <th
                                    className={`center sortable${sortConfig.key === 'status' ? ' sort-active' : ''}`}
                                    onClick={() => handleSort('status')}
                                    title="Ordena pelo estágio do ciclo de vida (aberta → parcial → concluída → cancelada)"
                                 >
                                    Situação{getSortIcon('status')}
                                 </th>
                              </tr>
                           </thead>
                           <tbody>
                              {orders.map((order) => {
                                 const itemCount = order.items?.length;
                                 return (
                                    <ClickableRow
                                       key={order.id}
                                       onClick={() =>
                                          navigate(
                                             `/inventory/transfers/${order.id}`
                                          )
                                       }
                                    >
                                       <td className="center">#{order.id}</td>
                                       <td>{order.origin_pdv_name ?? '—'}</td>
                                       <td>{order.dest_pdv_name ?? '—'}</td>
                                       <td className="num">
                                          {itemCount != null
                                             ? `${itemCount} ${itemCount === 1 ? 'item' : 'itens'}`
                                             : '—'}
                                       </td>
                                       <td>{order.created_by_name ?? '—'}</td>
                                       <td className="nowrap">
                                          {formatDateTimeBr(order.created_at)}
                                       </td>
                                       <td className="nowrap">
                                          {formatDateTimeBr(order.closed_at)}
                                       </td>
                                       <td className="center">
                                          <OrderStatusBadge
                                             value={order.status}
                                          />
                                       </td>
                                    </ClickableRow>
                                 );
                              })}
                           </tbody>
                        </DataTable>
                     </TableWrapper>

                     <PaginationBar
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        disabled={isLoading}
                     />
                  </>
               )}

               {!hasResults && !hasSearched && (
                  <EmptyState
                     icon={<FaExchangeAlt size={20} />}
                     title="Pesquise para começar"
                     description="Escolha situação e PDV e clique em Pesquisar — ou use um dos atalhos."
                     action={
                        <>
                           {shortcuts.map((shortcut) => (
                              <SmallButton
                                 key={shortcut.label}
                                 type="button"
                                 $intent="link"
                                 onClick={() => runSearch(shortcut.values)}
                              >
                                 {shortcut.label}
                              </SmallButton>
                           ))}
                        </>
                     }
                  />
               )}

               {!hasResults && hasSearched && !isLoading && (
                  <EmptyState
                     icon={<FaSearch size={18} />}
                     title="Nenhuma transferência encontrada"
                     description="Nenhuma ordem corresponde aos filtros aplicados."
                     action={
                        <GhostButton type="button" onClick={handleClear}>
                           Limpar filtros
                        </GhostButton>
                     }
                  />
               )}
            </SectionBody>
         </Section>
      </Container>
   );
}
